/**
 * UI string bundles. Welsh and English. Per the project's Welsh-language
 * rule (don't machine-translate; sound like real Welsh), these strings
 * are written natively in Welsh, not transliterated. Voice catalogue
 * labels still come from the backend in English — those are proper
 * nouns plus engineering descriptors and don't need translation tonight.
 *
 * Public API:
 *   const tr = makeTranslator('cy');
 *   tr('settings')              // → "Gosodiadau"
 *
 * Add a new key in both bundles or TypeScript will refuse to compile.
 */

export type UiLang = 'en' | 'cy';

type Bundle = {
  brand: string;
  settings: string;
  closeSettings: string;
  language: string;
  voice: string;
  speed: string;
  translateSelection: string;
  translateTo: string;
  readAlong: string;
  readAlongHint: string;
  pillIdle: string;
  pillPlaying: string;
  pillLoadingSpeak: string;
  pillLoadingTranslate: string;
  footerHint: string;
  languageEnglish: string;
  languageWelsh: string;
  poweredBy: string;
  pageContextToggle: string;
  pageContextHint: string;
};

const EN: Bundle = {
  brand: 'Llais',
  settings: 'Settings',
  closeSettings: 'Close settings',
  language: 'Language',
  voice: 'Voice',
  speed: 'Speed',
  translateSelection: 'Translate selection',
  translateTo: 'Translate to',
  readAlong: 'Read along (Welsh only)',
  readAlongHint: 'highlights each word',
  pillIdle: 'On — highlight text',
  pillPlaying: 'Playing',
  pillLoadingSpeak: 'Speaking…',
  pillLoadingTranslate: 'Translating…',
  footerHint: 'Highlight text on any page to play. Toggle Llais off to stop.',
  languageEnglish: 'English',
  languageWelsh: 'Cymraeg',
  poweredBy: 'Powered by',
  pageContextToggle: 'Use page context',
  pageContextHint: 'reads page language + title for better voices',
};

const CY: Bundle = {
  brand: 'Llais',
  settings: 'Gosodiadau',
  closeSettings: 'Cau gosodiadau',
  language: 'Iaith',
  voice: 'Llais',
  speed: 'Cyflymder',
  translateSelection: 'Cyfieithu\'r dewis',
  translateTo: 'Cyfieithu i',
  readAlong: 'Darllen ynghyd (Cymraeg yn unig)',
  readAlongHint: 'amlygu pob gair wrth ei lefaru',
  pillIdle: 'Ymlaen — amlygwch destun',
  pillPlaying: 'Yn chwarae',
  pillLoadingSpeak: 'Yn siarad…',
  pillLoadingTranslate: 'Yn cyfieithu…',
  footerHint: 'Amlygwch destun ar unrhyw dudalen i\'w chwarae. Diffoddwch Llais i stopio.',
  languageEnglish: 'English',
  languageWelsh: 'Cymraeg',
  poweredBy: 'Gan',
  pageContextToggle: 'Defnyddio cyd-destun y dudalen',
  pageContextHint: 'darllen iaith a theitl y dudalen i wella\'r lleisiau',
};

const BUNDLES: Record<UiLang, Bundle> = { en: EN, cy: CY };

export type TranslateFn = (key: keyof Bundle) => string;

export function makeTranslator(lang: UiLang): TranslateFn {
  const bundle = BUNDLES[lang] ?? EN;
  return (key) => bundle[key] ?? EN[key];
}

/**
 * Best-effort detection of the user's preferred UI language. If the page
 * declares lang="cy" / lang="cy-GB" (Welsh docs typically do), default
 * to Welsh. Otherwise default to English. The user can flip it via the
 * settings panel regardless.
 */
export function detectInitialLang(): UiLang {
  try {
    const htmlLang = document.documentElement.lang?.toLowerCase() ?? '';
    if (htmlLang === 'cy' || htmlLang.startsWith('cy-')) return 'cy';
  } catch {
    /* ignore */
  }
  return 'en';
}
